import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CADENCES } from '../../constants/cadences';
import { fmtWhen } from './ApprovalTable';

/**
 * Bottom sheet for one submission.
 * - 確認中 + canEdit: 何を (textarea) / どのくらい (cadence chips) editable, optional comment, 確定.
 * - 行動記録管理 (already 確定): read-only, shows the manager comment.
 * 提出の元 = what was live when the stylist submitted (previousActions). Not struck through.
 */
export default function ApprovalSheet({ row, isPending, canEdit, submitting, onClose, onApprove }) {
  const edit = canEdit && isPending;
  const base = isPending ? row.submittedActions || [] : row.approvedActions || row.submittedActions || [];
  // `orig` keeps a frequency that isn't one of the 3 chips (AI plans are saved as 「毎日」)
  // so it shows as selected instead of looking unset.
  const [draft, setDraft] = useState(() =>
    base.map((a) => ({
      actionId: a.actionId,
      name: a.name || '',
      cadence: a.cadence || CADENCES[2],
      orig: a.cadence,
      focusKey: a.focusKey || null, // kept through 確定 — the manager does not re-tag (v1)
    }))
  );
  const [note, setNote] = useState('');
  const prev = row.previousActions || [];

  const setName = (i, v) => setDraft((p) => p.map((a, j) => (j === i ? { ...a, name: v } : a)));
  const setCad = (i, v) => setDraft((p) => p.map((a, j) => (j === i ? { ...a, cadence: v } : a)));
  const removeAt = (i) => setDraft((p) => p.filter((_, j) => j !== i));
  const add = () => setDraft((p) => [...p, { actionId: undefined, name: '', cadence: CADENCES[2] }]);

  // Server rejects names under 2 chars (_valid_action_name); blank rows are dropped on send.
  const canSubmit =
    edit &&
    draft.some((a) => a.name.trim()) &&
    draft.every((a) => !a.name.trim() || a.name.trim().length >= 2) &&
    !submitting;

  // Rendered at the app-shell level: inside the scrolling page the tab bar sat on top of
  // the sheet and hid 確定.
  const host = typeof document !== 'undefined' ? document.querySelector('.kuroco-shell') || document.body : null;
  const sheet = (
    <div className="apv-mask" onClick={onClose} role="presentation">
      <div className="apv-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="apv-grab" />
        <div className="apv-sheet-h">
          <div>
            <h2>{row.stylist}</h2>
            <div className="apv-meta">
              {row.store} · 提出 {fmtWhen(row.submittedAt)}
              {row.reviewedAt ? ` · 確定 ${fmtWhen(row.reviewedAt)}` : ''}
              {row.reviewedByName ? `（${row.reviewedByName}）` : ''}
            </div>
          </div>
          <button type="button" className="apv-x" onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </div>

        <div className="apv-block">
          <div className="apv-lbl">{edit ? '今回の提出（直してよい）' : '確定した行動'}</div>
          {draft.map((a, i) => {
            const p = prev[i];
            return (
              <div className="apv-act" key={a.actionId || `new-${i}`}>
                <div className="apv-act-top">
                  <div className="apv-num">{i + 1}</div>
                  <div className="apv-lbl" style={{ margin: 0 }}>行動</div>
                  {edit && draft.length > 1 && (
                    <button type="button" className="apv-rm" onClick={() => removeAt(i)} aria-label="この行動を外す">
                      外す
                    </button>
                  )}
                </div>
                {p && (
                  <div className="apv-orig">
                    <div className="apv-orig-k">{isPending ? '提出の元（いま確定している内容）' : '前回の内容（確定前）'}</div>
                    <div className="apv-orig-row"><span className="apv-orig-l">何を</span><span className="apv-orig-v">{p.name}</span></div>
                    <div className="apv-orig-row"><span className="apv-orig-l">どのくらい</span><span className="apv-orig-v">{p.cadence || '—'}</span></div>
                  </div>
                )}
                <div className="apv-fl">何を（文面）</div>
                {edit ? (
                  <textarea className="apv-ta" value={a.name} maxLength={64} onChange={(e) => setName(i, e.target.value)} />
                ) : (
                  <div className="apv-orig-v" style={{ padding: '8px 0' }}>{a.name}</div>
                )}
                <div className="apv-fl">どのくらい（実施タイミング）</div>
                <div className="apv-cads">
                  {(a.orig && !CADENCES.includes(a.orig) ? [...CADENCES, a.orig] : CADENCES).map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`apv-cad${c === a.cadence ? ' is-on' : ''}`}
                      disabled={!edit}
                      onClick={() => edit && setCad(i, c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {edit && draft.length < 10 && (
            <button type="button" className="apv-add" onClick={add}>
              ＋ 行動を追加
            </button>
          )}
        </div>

        {edit ? (
          <div className="apv-block apv-footer">
            <div className="apv-lbl">マネージャーコメント（スタイリストへ）</div>
            <p className="apv-cap">任意。書けばスタイリストの「コメント」タブとAI相談へ渡る。空のまま確定してよい。</p>
            <textarea
              className="apv-ta"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例: 頻度は新規のお客様ごとに。まずは2週間これで。"
              maxLength={1000}
            />
            <button
              type="button"
              className="apv-btn"
              disabled={!canSubmit}
              onClick={() =>
                onApprove(
                  draft
                    .filter((a) => a.name.trim())
                    .map((a) => ({ actionId: a.actionId, name: a.name.trim(), cadence: a.cadence, focusKey: a.focusKey })),
                  note.trim()
                )
              }
            >
              {submitting ? '確定中…' : '確定'}
            </button>
            <p className="apv-hint">行動を直して確定。行は行動記録管理へ移ります。</p>
          </div>
        ) : (
          <div className="apv-block apv-footer">
            <div className="apv-lbl">マネージャーコメント</div>
            {row.managerNote ? <div className="apv-note">{row.managerNote}</div> : <p className="apv-cap">コメントなし</p>}
            {isPending && !canEdit && <p className="apv-hint">この提出を確定する権限がありません。</p>}
          </div>
        )}
      </div>
    </div>
  );
  return host ? createPortal(sheet, host) : sheet;
}
